import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Monitor, Smartphone, Laptop, Loader2 } from "lucide-react";
import { DeviceSession } from "@/hooks/useDeviceSession";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";

interface DeviceLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessions: DeviceSession[];
  onRemoveDevice: (sessionId: string) => Promise<boolean>;
  onRetry: () => void;
}

const getDeviceIcon = (deviceName: string | null) => {
  if (!deviceName) return Monitor;
  const name = deviceName.toLowerCase();
  if (name.includes("android") || name.includes("iphone") || name.includes("ios")) {
    return Smartphone;
  }
  if (name.includes("mac") || name.includes("windows") || name.includes("linux")) {
    return Laptop;
  }
  return Monitor;
};

export const DeviceLimitModal = ({
  isOpen,
  onClose,
  sessions,
  onRemoveDevice,
  onRetry,
}: DeviceLimitModalProps) => {
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleRemove = async (sessionId: string) => {
    setRemovingId(sessionId);
    const success = await onRemoveDevice(sessionId);
    setRemovingId(null);
    if (success) {
      onRetry();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-xl font-display">Device Limit Reached</DialogTitle>
          <DialogDescription>
            Your subscription allows a maximum of 2 devices. Remove a device below to sign in on this one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          {sessions.map((session) => {
            const Icon = getDeviceIcon(session.device_name);
            return (
              <div
                key={session.id}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
              >
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{session.device_name || "Unknown Device"}</p>
                    <p className="text-xs text-muted-foreground">
                      Last active {formatDistanceToNow(new Date(session.last_active_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleRemove(session.id)}
                  disabled={removingId === session.id}
                >
                  {removingId === session.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Remove"
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
